import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "../../utils/toast.js";
import { getCookie } from "../../utils/cookies.js";
import { getProfile } from "../../services/authService.js";
import { getSeatAvailability, createBooking } from "../../services/bookingService.js";

const POSITION_ROW_ORDER = ["driver", "front", "middle", "rear"];
const POSITION_LABELS = { driver: "Driver", front: "Front", middle: "Middle", rear: "Rear" };

function groupSeatsIntoRows(seats) {
  return POSITION_ROW_ORDER.map((position) => seats.filter((s) => s.position === position)).filter(
    (row) => row.length > 0,
  );
}

// route: the search result (has .car, .rateCard, .allowFullCabBooking, ._id).
// bookingType: "per-seat" | "full-cab". travelDate: "YYYY-MM-DD".
export default function BookingModal({ isOpen, route, bookingType, travelDate, onClose }) {
  const navigate = useNavigate();
  const isFullCab = bookingType === "full-cab";

  const [step, setStep] = useState(1);

  const [seatMap, setSeatMap] = useState([]);
  const [loadingSeats, setLoadingSeats] = useState(false);
  const [selectedSeats, setSelectedSeats] = useState([]);
  const [seatsError, setSeatsError] = useState("");

  const [selfProfile, setSelfProfile] = useState(null);
  const [bookedForSelf, setBookedForSelf] = useState(true);
  const [passengerName, setPassengerName] = useState("");
  const [passengerMobile, setPassengerMobile] = useState("");
  const [passengerEmail, setPassengerEmail] = useState("");
  const [passengerError, setPassengerError] = useState("");

  const [pickupLocation, setPickupLocation] = useState("");
  const [locating, setLocating] = useState(false);
  const [pickupError, setPickupError] = useState("");

  const [paymentOption, setPaymentOption] = useState("full");
  const [submitting, setSubmitting] = useState(false);
  const [confirmedBooking, setConfirmedBooking] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    setStep(1);
    setSelectedSeats([]);
    setSeatsError("");
    setBookedForSelf(true);
    setPassengerError("");
    setPickupLocation("");
    setPickupError("");
    setPaymentOption("full");
    setConfirmedBooking(null);

    if (!isFullCab) fetchSeats();

    getProfile()
      .then((res) => {
        if (res.success && res.data) {
          const profile = {
            name: res.data.name || "",
            mobile: res.data.mobile || "",
            email: res.data.email || "",
          };
          setSelfProfile(profile);
          setPassengerName(profile.name);
          setPassengerMobile(profile.mobile);
          setPassengerEmail(profile.email);
        }
      })
      .catch(() => {
        let cookieUser = null;
        try {
          cookieUser = JSON.parse(getCookie("user"));
        } catch {
          cookieUser = null;
        }
        const profile = {
          name: cookieUser?.name || "",
          mobile: cookieUser?.mobile || "",
          email: "",
        };
        setSelfProfile(profile);
        setPassengerName(profile.name);
        setPassengerMobile(profile.mobile);
        setPassengerEmail(profile.email);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, route, bookingType, travelDate]);

  async function fetchSeats() {
    setLoadingSeats(true);
    setSeatsError("");
    try {
      const res = await getSeatAvailability(route._id, travelDate);
      if (!res.success) {
        setSeatsError(res.message || "Couldn't load seats.");
        return;
      }
      setSeatMap((res.data || []).filter((s) => s.status !== "blocked"));
    } catch (err) {
      setSeatsError(err.response?.data?.message || "Couldn't load seats.");
    } finally {
      setLoadingSeats(false);
    }
  }

  if (!isOpen || !route) return null;

  function toggleSeat(seat) {
    if (seat.status !== "available" && !selectedSeats.includes(seat.seatNumber)) return;
    setSelectedSeats((prev) =>
      prev.includes(seat.seatNumber)
        ? prev.filter((n) => n !== seat.seatNumber)
        : [...prev, seat.seatNumber],
    );
  }

  function seatClassName(seat) {
    if (seat.status === "driver") return "seat-btn driver";
    if (selectedSeats.includes(seat.seatNumber)) return "seat-btn selected";
    if (seat.status === "booked") return "seat-btn booked";
    return "seat-btn available";
  }

  function handleSeatsNext() {
    if (!isFullCab && selectedSeats.length === 0) {
      setSeatsError("Please select at least one seat.");
      return;
    }
    setSeatsError("");
    setStep(2);
  }

  function selectSelf() {
    setBookedForSelf(true);
    if (selfProfile) {
      setPassengerName(selfProfile.name);
      setPassengerMobile(selfProfile.mobile);
      setPassengerEmail(selfProfile.email);
    }
  }

  function selectOther() {
    setBookedForSelf(false);
    setPassengerName("");
    setPassengerMobile("");
    setPassengerEmail("");
  }

  function handlePassengerNext() {
    if (!passengerName.trim() || !/^\d{10}$/.test(passengerMobile.trim())) {
      setPassengerError("Please enter a name and a 10-digit mobile number.");
      return;
    }
    setPassengerError("");
    setStep(3);
  }

  function useCurrentLocation() {
    if (!navigator.geolocation) {
      toast.error("Location isn't available in this browser.");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setPickupLocation(`📍 Current location (${latitude.toFixed(4)}, ${longitude.toFixed(4)})`);
        setLocating(false);
      },
      () => {
        toast.error("Couldn't fetch your location. Please enter it manually.");
        setLocating(false);
      },
    );
  }

  function handlePickupNext() {
    if (!pickupLocation.trim()) {
      setPickupError("Please share a pickup location.");
      return;
    }
    setPickupError("");
    setStep(4);
  }

  const rateCard = route.rateCard || {};
  const tripFare = isFullCab
    ? rateCard.fullCab || 0
    : selectedSeats.reduce((sum, seatNumber) => {
        const seat = seatMap.find((s) => s.seatNumber === seatNumber);
        return sum + (seat ? rateCard[seat.position] || 0 : 0);
      }, 0);
  const payableNow = paymentOption === "advance" ? Math.round(tripFare * 0.25) : tripFare;

  async function handleConfirm() {
    setSubmitting(true);
    try {
      const res = await createBooking({
        routeId: route._id,
        travelDate,
        bookingType,
        selectedSeats: isFullCab ? [] : selectedSeats,
        bookedForSelf,
        passengerName: passengerName.trim(),
        passengerMobile: passengerMobile.trim(),
        passengerEmail: passengerEmail.trim(),
        pickupLocation: pickupLocation.trim(),
      });
      if (!res.success) {
        toast.error(res.message || "Couldn't confirm the booking.");
        return;
      }
      setConfirmedBooking(res.data);
    } catch (err) {
      toast.error(err.response?.data?.message || "Couldn't confirm the booking.");
    } finally {
      setSubmitting(false);
    }
  }

  const soldOut =
    !isFullCab && !loadingSeats && !seatsError && seatMap.length > 0 &&
    seatMap.every((s) => s.status !== "available");

  const selectedPositionLabels = selectedSeats
    .map((seatNumber) => {
      const seat = seatMap.find((s) => s.seatNumber === seatNumber);
      return seat ? POSITION_LABELS[seat.position] : null;
    })
    .filter(Boolean);

  return (
    <div className="wizard-overlay open">
      <div className="wizard-modal">
        <button type="button" className="wizard-close" onClick={onClose} aria-label="Close">
          ×
        </button>

        {confirmedBooking ? (
          <div className="confirm-screen">
            <div className="confirm-icon">✅</div>
            <h2>Booking Confirmed!</h2>
            <p className="confirm-order">
              Order <strong>#{String(confirmedBooking._id).slice(-8).toUpperCase()}</strong>
            </p>
            <div className="confirm-summary">
              <div className="price-row">
                <span>Route</span>
                <span>
                  {route.fromCity} → {route.toCity}
                </span>
              </div>
              <div className="price-row">
                <span>Travel date</span>
                <span>{travelDate}</span>
              </div>
              <div className="price-row">
                <span>{isFullCab ? "Booking type" : "Seats"}</span>
                <span>{isFullCab ? "Full cab" : selectedPositionLabels.join(", ")}</span>
              </div>
              <div className="price-row price-total">
                <span>Paid now</span>
                <span>₹{payableNow}</span>
              </div>
            </div>
            <div className="confirm-actions">
              <button type="button" className="btn btn-outline" onClick={onClose}>
                Close
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  onClose();
                  navigate("/panel_customer/booking");
                }}
              >
                View My Bookings
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="wizard-steps">
              <div className={`wizard-step-pill ${step === 1 ? "active" : step > 1 ? "done" : ""}`}>
                <span>1</span> Seats
              </div>
              <div className={`wizard-step-pill ${step === 2 ? "active" : step > 2 ? "done" : ""}`}>
                <span>2</span> Passenger
              </div>
              <div className={`wizard-step-pill ${step === 3 ? "active" : step > 3 ? "done" : ""}`}>
                <span>3</span> Pickup
              </div>
              <div className={`wizard-step-pill ${step === 4 ? "active" : ""}`}>
                <span>4</span> Payment
              </div>
            </div>

            {step === 1 && (
              <div className="wizard-panel active">
                <h2>Choose your seats</h2>
                <p className="wizard-sub">
                  {isFullCab
                    ? "You're booking the entire cab — every seat is yours."
                    : "Tap a seat to select it."}
                </p>

                {isFullCab ? (
                  <div className="full-cab-note">
                    🚙 Full cab booking — no individual seats to choose, the whole car is reserved
                    for you.
                  </div>
                ) : loadingSeats ? (
                  <p className="wizard-hint">Loading seat map...</p>
                ) : soldOut ? (
                  <div className="empty-state show">
                    <h3>😔 Sold out</h3>
                    <p>Every seat on this route is already booked for {travelDate}. Try a different date or route.</p>
                  </div>
                ) : (
                  <>
                    <div className="seat-map">
                      {groupSeatsIntoRows(seatMap).map((row, i) => (
                        <div className="seat-row" key={i}>
                          {row.map((seat) =>
                            seat.status === "driver" ? (
                              <div
                                className="seat-btn driver"
                                key={seat.seatNumber}
                                title="Driver seat"
                              >
                                D
                              </div>
                            ) : (
                              <button
                                type="button"
                                key={seat.seatNumber}
                                className={seatClassName(seat)}
                                disabled={seat.status === "booked"}
                                onClick={() => toggleSeat(seat)}
                                title={seat.status === "booked" ? "Already booked" : "Tap to select"}
                              >
                                {POSITION_LABELS[seat.position]?.[0] || seat.seatNumber}
                              </button>
                            ),
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="seat-legend">
                      <span>
                        <span className="seat-swatch available" /> Available
                      </span>
                      <span>
                        <span className="seat-swatch selected" /> Selected
                      </span>
                      <span>
                        <span className="seat-swatch booked" /> Booked
                      </span>
                      <span>
                        <span className="seat-swatch driver" /> Driver
                      </span>
                    </div>
                    <p className="selection-summary">
                      {selectedSeats.length
                        ? `${selectedSeats.length} seat(s) selected: ${selectedPositionLabels.join(", ")}`
                        : "No seats selected"}
                    </p>
                  </>
                )}
                <p className="field-error">{seatsError}</p>

                <div className="wizard-actions">
                  <button type="button" className="btn btn-outline" onClick={onClose}>
                    {soldOut ? "Close" : "Cancel"}
                  </button>
                  {!soldOut && (
                    <button type="button" className="btn btn-primary" onClick={handleSeatsNext}>
                      Next: Passenger Details →
                    </button>
                  )}
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="wizard-panel active">
                <h2>Passenger details</h2>
                <p className="wizard-sub">Who are we booking this ride for?</p>

                <div className="checkbox-row" onClick={selectSelf} style={{ cursor: "pointer" }}>
                  <input type="radio" checked={bookedForSelf} onChange={selectSelf} />
                  <span>
                    <strong>Myself</strong> — using your profile details
                  </span>
                </div>
                <div className="checkbox-row" onClick={selectOther} style={{ cursor: "pointer" }}>
                  <input type="radio" checked={!bookedForSelf} onChange={selectOther} />
                  <span>
                    <strong>Someone else</strong> — enter their details below
                  </span>
                </div>

                <div className="field">
                  <label htmlFor="passengerName">Full name *</label>
                  <input
                    type="text"
                    id="passengerName"
                    placeholder="e.g. Riya Sharma"
                    value={passengerName}
                    disabled={bookedForSelf}
                    onChange={(e) => setPassengerName(e.target.value)}
                  />
                </div>
                <div className="field">
                  <label htmlFor="passengerMobile">Mobile (WhatsApp) *</label>
                  <input
                    type="tel"
                    id="passengerMobile"
                    placeholder="10-digit mobile number"
                    inputMode="numeric"
                    maxLength={10}
                    value={passengerMobile}
                    disabled={bookedForSelf}
                    onChange={(e) =>
                      setPassengerMobile(e.target.value.replace(/\D/g, "").slice(0, 10))
                    }
                  />
                </div>
                <div className="field">
                  <label htmlFor="passengerEmail">
                    Email <span className="optional-tag">(for invoice)</span>
                  </label>
                  <input
                    type="email"
                    id="passengerEmail"
                    placeholder="you@example.com"
                    value={passengerEmail}
                    disabled={bookedForSelf}
                    onChange={(e) => setPassengerEmail(e.target.value)}
                  />
                </div>
                <p className="field-error">{passengerError}</p>

                <div className="wizard-actions">
                  <button type="button" className="btn btn-outline" onClick={() => setStep(1)}>
                    ← Back
                  </button>
                  <button type="button" className="btn btn-primary" onClick={handlePassengerNext}>
                    Next: Pickup Location →
                  </button>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="wizard-panel active">
                <h2>Pickup location</h2>
                <p className="wizard-sub">Where should the driver pick you up?</p>

                <div className="wizard-actions" style={{ marginTop: 0, marginBottom: 16 }}>
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={useCurrentLocation}
                    disabled={locating}
                  >
                    📍 {locating ? "Locating..." : "Use my current location"}
                  </button>
                </div>

                <div className="field">
                  <label htmlFor="pickupLocation">Or enter pickup address manually</label>
                  <textarea
                    id="pickupLocation"
                    rows={3}
                    placeholder="House / street / landmark, area, city"
                    value={pickupLocation}
                    onChange={(e) => setPickupLocation(e.target.value)}
                  />
                </div>
                <p className="field-error">{pickupError}</p>

                <div className="wizard-actions">
                  <button type="button" className="btn btn-outline" onClick={() => setStep(2)}>
                    ← Back
                  </button>
                  <button type="button" className="btn btn-primary" onClick={handlePickupNext}>
                    Next: Payment →
                  </button>
                </div>
              </div>
            )}

            {step === 4 && (
              <div className="wizard-panel active">
                <h2>Payment</h2>
                <p className="wizard-sub">Choose how you'd like to pay.</p>

                <label className={`payment-option ${paymentOption === "full" ? "checked" : ""}`}>
                  <input
                    type="radio"
                    checked={paymentOption === "full"}
                    onChange={() => setPaymentOption("full")}
                  />
                  <span>
                    <strong>Pay full now</strong>
                    <span>Pay the entire trip fare online right now</span>
                  </span>
                </label>
                <label className={`payment-option ${paymentOption === "advance" ? "checked" : ""}`}>
                  <input
                    type="radio"
                    checked={paymentOption === "advance"}
                    onChange={() => setPaymentOption("advance")}
                  />
                  <span>
                    <strong>25% advance · rest to driver</strong>
                    <span>Pay a small advance online, settle the balance with the driver</span>
                  </span>
                </label>

                <div className="price-breakdown">
                  <strong>Price breakdown</strong>
                  <div className="price-row">
                    <span>{isFullCab ? "Booking type" : "Seats"}</span>
                    <span>{isFullCab ? "Full cab" : `${selectedSeats.length} seat(s)`}</span>
                  </div>
                  <div className="price-row">
                    <span>Trip fare</span>
                    <span>₹{tripFare}</span>
                  </div>
                  <div className="price-row">
                    <span>Convenience fee</span>
                    <span>
                      <s>₹49</s> Free
                    </span>
                  </div>
                  <div className="price-row price-total">
                    <span>Payable now</span>
                    <span>₹{payableNow}</span>
                  </div>
                </div>

                <div className="wizard-actions">
                  <button type="button" className="btn btn-outline" onClick={() => setStep(3)}>
                    ← Back
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleConfirm}
                    disabled={submitting}
                  >
                    {submitting ? "Confirming..." : "Confirm Booking ✓"}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
